import { useEffect, useLayoutEffect, useSyncExternalStore, type ReactNode } from 'react';
import { applyColorMode, getColorMode } from '@doo-events/design-tokens/color-mode';
import { applyEventAccent, clearEventAccent } from '@doo-events/design-tokens/event-theme';

/**
 * Área do participante (fila e vitrine): fica SEMPRE no escuro, sobre a imagem de fundo do
 * evento, com o texto na cor que o organizador escolheu — não segue o switch do admin.
 *
 * O `.dark` vai no <html>, e não num contêiner, para os modais (portal no <body>) e os toasts
 * herdarem. Ao sair da rota, volta o modo que a pessoa escolheu no switch.
 */
export function IlhaEscura({ children }: { children: ReactNode }) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('dark');
    return () => applyColorMode(getColorMode(), root);
  }, []);
  return <>{children}</>;
}

function assinarHtml(aoMudar: () => void) {
  const obs = new MutationObserver(aoMudar);
  obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  return () => obs.disconnect();
}

/** O modo efetivo do <html> (switch ou ilha), para quem precisa dele em JS — ex.: o sonner. */
export function useModoEfetivo(): 'light' | 'dark' {
  return useSyncExternalStore(
    assinarHtml,
    () => (document.documentElement.classList.contains('dark') ? 'dark' : 'light'),
    () => 'light'
  );
}

/**
 * Publica a cor principal do organizador no contrato do pacote (`--event-*` no <html>), para
 * os componentes do design system da área do participante (foco, `.btn-primary`) seguirem o
 * evento. Cor sem contraste ≥ 4,5:1 com texto é recusada: fica a cor da marca. Os botões que
 * já usam `corPrincipal`/`corTexto` direto continuam com a escolha do organizador.
 */
export function useCorDoEvento(cor: string | undefined) {
  useEffect(() => {
    const root = document.documentElement;
    applyEventAccent(root, cor);
    return () => clearEventAccent(root);
  }, [cor]);
}
